import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '../components/ui'
import { PageHeader } from '../../ui'
import { useIXVerifyAuth } from '../contexts'
import { listVerifications } from '../lib/ixverifyApi'

interface DashboardStats {
  todayCount: number
  weekCount: number
  monthCount: number
  matchRate: number | null
  totalVerifications: number
}

export function Dashboard() {
  const { user, supabaseUser, loading: authLoading } = useIXVerifyAuth()
  const displayName = user?.full_name || supabaseUser?.user_metadata?.full_name || 'User'
  const [stats, setStats] = useState<DashboardStats>({
    todayCount: 0,
    weekCount: 0,
    monthCount: 0,
    matchRate: null,
    totalVerifications: 0,
  })
  const [loading, setLoading] = useState(true)


  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // Fetch verifications (site-scoped when the user has a site).
        const { data: verifications, error } = await listVerifications({ siteId: user.site_id || undefined })


        if (cancelled) {
          return
        }
        if (error) throw error

        const now = new Date()
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const startOfWeek = new Date(startOfToday)
        startOfWeek.setDate(startOfWeek.getDate() - 7)
        const startOfMonth = new Date(startOfToday)
        startOfMonth.setMonth(startOfMonth.getMonth() - 1)

        const allVerifications = verifications || []

        // Calculate stats
        const todayCount = allVerifications.filter(
          (v) => new Date(v.created_at) >= startOfToday
        ).length

        const weekCount = allVerifications.filter(
          (v) => new Date(v.created_at) >= startOfWeek
        ).length

        const monthCount = allVerifications.filter(
          (v) => new Date(v.created_at) >= startOfMonth
        ).length

        // Calculate match rate (exclude pending)
        const completedVerifications = allVerifications.filter(
          (v) => v.match_result !== 'pending'
        )
        const matchRate = completedVerifications.length > 0
          ? (completedVerifications.filter((v) => v.match_result === 'match').length / completedVerifications.length) * 100
          : null

        if (!cancelled) {
          setStats({
            todayCount,
            weekCount,
            monthCount,
            matchRate,
            totalVerifications: allVerifications.length,
          })
        }
      } catch (error) {
        console.error('[Dashboard] Error fetching dashboard data:', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    // Always return cleanup function to properly handle unmount
    // Wait for auth to finish loading before fetching data
    if (authLoading) {
      return () => {
        cancelled = true
      }
    }

    if (user?.id) {
      fetchData()
    } else {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [user?.id, user?.site_id, authLoading])

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Welcome header */}
      <PageHeader
        title={`Welcome back, ${displayName}`}
        subtitle="Ready to verify a new IP dispensation?"
      />

      {/* Prominent New Verification Button */}
      <Link to="/ixverify/verify" className="block">
        <div className="bg-primary-600 rounded-xl p-6 text-white transition-colors duration-150 hover:bg-primary-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-1">Start New Verification</h2>
              <p className="text-primary-100 text-sm">
                Capture IXRS and Medication photos to verify drug dispensation.
              </p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-primary-100">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Tap to open camera
            <svg className="w-5 h-5 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </Link>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            {loading ? (
              <div className="h-8 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin dark:border-primary-400 dark:border-t-transparent"></div>
              </div>
            ) : (
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.todayCount}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            {loading ? (
              <div className="h-8 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin dark:border-primary-400 dark:border-t-transparent"></div>
              </div>
            ) : (
              <p className={`text-2xl font-bold ${stats.matchRate !== null && stats.matchRate < 80 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {stats.matchRate !== null ? `${Math.round(stats.matchRate)}%` : '-'}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">Match Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            {loading ? (
              <div className="h-8 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin dark:border-primary-400 dark:border-t-transparent"></div>
              </div>
            ) : (
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.weekCount}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">This Week</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/ixverify/verify">
          <Card className="hover:border-primary-300 hover:bg-primary-50 transition-colors duration-150 cursor-pointer h-full dark:hover:border-primary-700 dark:hover:bg-primary-900/20">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2 dark:bg-primary-900/40">
                <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-medium text-gray-900 text-sm dark:text-gray-100">Verify Drug</p>
            </CardContent>
          </Card>
        </Link>
        {/* Shipment Verification - Hidden until feature is complete
        <Link to="/ixverify/shipment">
          <Card className="hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer h-full dark:hover:border-primary-700 dark:hover:bg-primary-900/20">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2 dark:bg-primary-900/40">
                <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="font-medium text-gray-900 text-sm">Verify Shipment</p>
            </CardContent>
          </Card>
        </Link>
        */}
        <Link to="/ixverify/history">
          <Card className="hover:border-gray-300 hover:bg-gray-50 transition-colors duration-150 cursor-pointer h-full dark:hover:border-gray-600 dark:hover:bg-gray-700/50">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2 dark:bg-gray-700">
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-medium text-gray-900 text-sm dark:text-gray-100">History</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Monthly Summary */}
      {!loading && stats.totalVerifications > 0 && (
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-gray-900 mb-4 dark:text-gray-100">Monthly Summary</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3 dark:bg-gray-900/40">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.monthCount}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Verifications (30 days)</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 dark:bg-gray-900/40">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalVerifications}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">All Time</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
